import * as React from 'react';

import { observer } from 'mobx-react';

import { FormStore } from '@estee/elc-form';
import { IFormValues } from '@estee/elc-form/typings/views/Form';

import { FormContext } from '~context/FormContext';
import { IFormItem } from '~domain/entities/Config';
import { useTranslation } from '~utils/hooks/translations';

import { AccordionCheckboxGroup } from './AccordionCheckboxGroup';
import { ApiDropDown } from './ApiDropDown';
import { Checkbox } from './Checkbox';
import { CheckboxGroup } from './CheckboxGroup';
import { CmsDropDown } from './CmsDropDown';
import { DateDay } from './DateDay';
import { DateMonth } from './DateMonth';
import { DateYear } from './DateYear';
import { DropDown } from './DropDown';
import { EmailInput } from './EmailInput';
import { Fieldset } from './Fieldset';
import ForgotPassword from './ForgotPassword';
import { LanguageSwitcher } from './LanguageSwitcher';
import { withPlaceholder } from './NumberInput';
import { Password } from './Password';
import { PasswordCriteria } from './PasswordCriteria';
import { PasswordStrength } from './PasswordStrength';
import { PrivacyNotice } from './PrivacyNotice';
import { TermsConditions } from './TermsConditions';
import { PhoneInput } from './PhoneInput';
import { Text } from './Text';
import { TextInput } from './TextInput';
import { TextLink } from './TextLink';
import { Title } from './Title';

export interface IFormItemAdapterProps extends IFormItem {
    labelKey: string;
    formStore?: FormStore<IFormValues>;
    source?: { type: string; value: string };
}

const componentRegistry = {
    checkbox: Checkbox,
    checkboxGroup: CheckboxGroup,
    accordionCheckboxGroup: AccordionCheckboxGroup,
    fieldset: Fieldset,
    select: DropDown,
    selectCms: CmsDropDown,
    selectApi: ApiDropDown,
    text: TextInput,
    tel: TextInput,
    dateDay: DateDay,
    dateMonth: DateMonth,
    dateYear: DateYear,
    password: Password,
    passwordStrength: PasswordStrength,
    passwordCriteria: PasswordCriteria,
    email: EmailInput,
    label: Text,
    title: Title,
    textLink: TextLink,
    languageSwitcher: LanguageSwitcher,
    phoneInput: PhoneInput,
    birthYear: withPlaceholder('YYYY'),
    birthMonth: withPlaceholder('MM'),
    birthDay: withPlaceholder('DD'),
    termsAndConditions: TermsConditions,
    forgotPassword: ForgotPassword,
    privacyNotice: PrivacyNotice
};

export const FormItemAdapter = observer((props: IFormItemAdapterProps) => {
    const { inputType, name, labelKey, hiddenText = '' } = props;
    const Component = componentRegistry[inputType] as React.ElementType;
    const translations = useTranslation();
    const { fieldsValidations, readonlyFields, highlightedFields } = React.useContext(FormContext);
    const readonly = props.readonly || readonlyFields.has(name);
    const highlighted = highlightedFields.has(name);
    const fieldValidations = fieldsValidations?.[name] ?? {};

    const composeProps = React.useMemo(() => {
        let componentProps;
        switch (inputType) {
            case 'dateDay':
            case 'dateMonth':
            case 'dateYear':
            case 'select': {
                componentProps = {
                    ...props,
                    selectLabel: translations.selectLabel
                };
                break;
            }
            case 'selectCms': {
                const { source } = props;
                const dataList = (source?.type === 'cms' && translations[source?.value]) || '';
                componentProps = {
                    ...props,
                    selectLabel: translations.selectLabel,
                    dataList
                };
                break;
            }
            case 'privacyNotice': {
                componentProps = {
                    ...props,
                    hiddenText: translations[hiddenText],
                    labelKey
                };
                break;
            }
            case 'passwordCriteria': {
                componentProps = {
                    ...props,
                    translations
                };
                break;
            }
            default: {
                componentProps = { ...props };
            }
        }

        return {
            ...componentProps,
            inputLabel: translations[labelKey],
            readonly,
            highlighted,
            fieldValidations
        };
    }, [fieldValidations, readonly, highlighted]);

    return (
        Component && (
            <React.Suspense fallback={null}>
                <Component {...composeProps} />
            </React.Suspense>
        )
    );
});
